import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Navbar, Nav, Modal } from "react-bootstrap";
import Logo from "../../assets/images/logo.svg";
import AIFace from "../../assets/images/ai-loader.png";
import RightDropdownMenu from "./RightDropdownMenu";
import { useDispatch, useSelector } from "react-redux";
import ConfirmSubscriptionModal from "../Login/Subscription/BuySubscriptionApprovalModal";
import ProfileUnderReviewModal from "../Common/ProfileDetails/ProfileUnderReviewModal";
import {
  ACCOUNT_TYPE,
  LOGIN_TYPE,
  SUBSCRIPTION_BUY_MODE,
} from "../../assets/constants/constants";
import AddOnConfirmationModal from "../Login/Subscription/AddOnConfirmationModal";
import { toast } from "react-toastify";
import WebService from "../../Services/WebService";
import { Addon, SubscriptionFlags } from "../Login/interface";
import Payment from "../Login/StripePayment/StripePaymentForm";
import {
  profileCompletionFlags,
  RegistrationIds,
} from "../../assets/interface/primeSharkWebsite_interface";
import "./header.scss";
import { registrationId, storeAIScore } from "../../config/action";
import NotificationBell from "./Notification";
import HelperService from "../../Services/HelperService";
import { MdAutoAwesome } from "react-icons/md";

const LightbulbIcon = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#caa542" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9, marginBottom: '2px' }}>
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M12 2v1" />
    <path d="M12 14c-1.66 0-3-1.34-3-3 0-1.28.84-2.4 2-2.82V7c0-1.1.9-2 2-2s2 .9 2 2v1.18c1.16.42 2 1.54 2 2.82 0 1.66-1.34 3-3 3z" />
    <line x1="4.93" y1="4.93" x2="5.64" y2="5.64" />
    <line x1="19.07" y1="4.93" x2="18.36" y2="5.64" />
    <line x1="2" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="22" y2="12" />
    <line x1="4.93" y1="19.07" x2="5.64" y2="18.36" />
    <line x1="19.07" y1="19.07" x2="18.36" y2="18.36" />
  </svg>
);

const StarsIcon = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
    <svg width="10" height="10" viewBox="0 0 24 24" fill="#caa542" stroke="none">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="#caa542" stroke="none">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
    <svg width="10" height="10" viewBox="0 0 24 24" fill="#caa542" stroke="none">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  </div>
);

const DIAL_TICK_COUNT = 60;
const DIAL_TICKS = Array.from({ length: DIAL_TICK_COUNT }).map((_, i) => {
  const angle = (i * 360) / DIAL_TICK_COUNT;
  const rad = (angle * Math.PI) / 180;
  const cx = 100;
  const cy = 100;
  const outerR = 98;
  const innerR = i % 5 === 0 ? 92 : 95; // some lines, some dots
  const x1 = cx + outerR * Math.cos(rad);
  const y1 = cy + outerR * Math.sin(rad);
  const x2 = cx + innerR * Math.cos(rad);
  const y2 = cy + innerR * Math.sin(rad);
  return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#caa542" strokeWidth="1.2" strokeLinecap="round" />;
});

const getProfileStatus = (score: number, strength: string) => {
  const greenMetalColors = {
    "--metal-1": "#f4ffe9",
    "--metal-2": "#bfe3a0",
    "--metal-3": "#7fc45c",
    "--metal-4": "#4c8a34",
    "--metal-5": "#2a5a1c",
  } as React.CSSProperties;

  // Support 1000-based scale (and legacy 100-based scale)
  const normScore = score <= 100 ? score * 10 : score;
  const derivedStrength =
    strength ||
    (normScore >= 850
      ? "Expert"
      : normScore >= 700
      ? "Strong"
      : normScore >= 550
      ? "Moderate"
      : normScore >= 400
      ? "Basic"
      : "Weak");

  const st = (derivedStrength || "weak").toLowerCase();
  const labelText = HelperService.ProperCase(derivedStrength || "Weak");

  if (st === "expert") {
    return {
      label: labelText,
      ringColor: "#9bf7ac",
      textColor: "#f0c860",
      gradientColors: ["#4CAF50", "#9bf7ac"],
      metalColors: greenMetalColors
    };
  }
  if (st === "strong") {
    return {
      label: labelText,
      ringColor: "#9bf7ac",
      textColor: "#f0c860",
      gradientColors: ["#4CAF50", "#9bf7ac"],
      metalColors: greenMetalColors
    };
  }
  if (st === "moderate") {
    return {
      label: labelText,
      ringColor: "#9bf7ac",
      textColor: "#f0c860",
      gradientColors: ["#4CAF50", "#9bf7ac"],
      metalColors: greenMetalColors
    };
  }
  if (st === "basic") {
    return {
      label: labelText,
      ringColor: "#9bf7ac",
      textColor: "#f0c860",
      gradientColors: ["#4CAF50", "#9bf7ac"],
      metalColors: greenMetalColors
    };
  }

  return {
    label: labelText,
    ringColor: "#9bf7ac",
    textColor: "#f0c860",
    gradientColors: ["#4CAF50", "#9bf7ac"],
    metalColors: greenMetalColors
  };
};

// Animates a number from its previous value up to `target` over `duration`ms.
function useCountUp(target: number, duration = 1100) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf: number;
    const start = performance.now();
    const from = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      setDisplay(Math.round(from + (target - from) * p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return display;
}

const Header = () => {
  const activeLoginTypes = useSelector(
    (state: any) => state?.items?.storeActiveLoginType,
  );
  let account_type = activeLoginTypes?.account_type;
  let login_type = activeLoginTypes?.loginType;

  const [showProfileReviewModal, setShowProfileReviewModal] = useState(false);
  const isInvestorUnderReview =
    login_type?.toLowerCase() === LOGIN_TYPE.INVESTOR.toLowerCase() &&
    localStorage.getItem("is_investor_profile_verified") !== "VERIFIED";

  let selectedTab = localStorage.getItem("Tab_Selected") ?? "Investor";
  const [showModal, setShowModal] = useState(false);
  const [availableAdons, setavailableAdons] = useState<Addon[]>([]);
  const [conversionRate, setConversionRate] = useState(1);
  const [currencyCode, setCurrencyCode] = useState("");
  const [showPaymentForm, setShowPaymentForm] = useState(true);
  const [buySubscriptionDetails, setBuySubscriptionDetails] = useState({
    plan_name: "",
    client_secret: "",
    payment_intent: "",
    trn_id: "",
    trans_amount: "",
    exchange_rate: "",
    currency_code: "",
  });
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const navigate = useNavigate();
  let dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const storeTabDeails = (tab: string) => {
    localStorage.setItem("Tab_Selected", tab);
    localStorage.setItem("view", "gridView");
  };
  const access_token_available = localStorage.getItem("access_token");

  const profile = useSelector((state: any) => state?.items?.profileDetail);
  const hasSubscription = profile?.user_subscription;
  const has_want_to_grow_access =
    profile?.user_subscription?.has_want_to_grow ?? 0;
  const [subscriptionPlanUUID, setSubscriptionPlanUUID] = useState("");
  const subscriptionFlags: SubscriptionFlags = useSelector(
    (state: any) => state?.items?.subscriptionFlags,
  );
  const [modalMessage, setModalMessage] = useState("");
  const [subscriptionMode, setSubscriptionMode] = useState("");
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [myProposalCount, setMyProposalCount] = useState(0);
  const storeProfileCompletion: profileCompletionFlags = useSelector(
    (state: any) => state?.items?.profileCompletionFlags,
  );
  // const [aiScore, setAIScore] = useState(0);
  // const [aiStrength, setAIStrength] = useState("");

  const [aiProfiles, setAIProfiles] = useState<
    {
      profile: string;
      label: string;
      score: number;
      max_score?: number;
      strength: string;
    }[]
  >([]);

  const frontScore = useCountUp(aiProfiles[0]?.score ?? 0);
  const backScore = useCountUp(aiProfiles[1]?.score ?? 0);
  const [visibleAIIndex, setVisibleAIIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    if (aiProfiles.length < 2) return;

    const interval = setInterval(() => {
      setIsFlipped(prev => !prev);
    }, 3000);

    return () => clearInterval(interval);
  }, [aiProfiles]);



  const aiRouteMap: Record<string, string> = {
    investment: "investment",
    "want-to-grow": "want-to-grow",
    "want-to-sell": "want-to-sell",
  };
  const AI_PROFILE_LABELS: Record<string, string> = {
    "investment": "Capital Bridge",
    "want-to-grow": "Global Connect",
    "want-to-sell": "Business Exchange",
  };
  const handleAINavigation = (profileKey: string) => {
    if (!profileKey) return;
    console.log("Navigating to profile analysis for:", profileKey);
    if (profileKey) {
      navigate(
        `${login_type}/${profileKey}/profile-analysis`
      );
    } else {
      navigate(
        `${login_type}/profile-analysis`
      );
    }
  };


  let {
    has_investment_profile_completed,
    has_investment_subscription,
    has_want_to_sell_subscription,
    has_want_to_sell_profile_completed,
    has_want_to_grow_subscription,
    has_want_to_grow_profile_completed,
  } = storeProfileCompletion;
  const incompleteProfiles: string[] = [];

  if (has_investment_subscription && !has_investment_profile_completed) {
    incompleteProfiles.push("Capital Bridge");
  }
  if (has_want_to_sell_subscription && !has_want_to_sell_profile_completed) {
    incompleteProfiles.push("Business Exchange");
  }
  if (has_want_to_grow_subscription && !has_want_to_grow_profile_completed) {
    incompleteProfiles.push("Global Connect");
  }


  const handleGlobalconnectNavigation = () => {
    storeTabDeails("global-connect");
    navigate(
      `${login_type?.toLowerCase()}/${account_type?.toLocaleLowerCase()}/global-connect`,
    );
  };
  const handleYes = () => {
    navigate(
      `${login_type?.toLowerCase()}/${account_type?.toLowerCase()}/subscriptions?mode=${subscriptionMode}`,
    );
  };
  const handleYesForAddOn = () => {
    BuyPlan(0);
  };
  const BuyPlan = (index: number) => {
    if (availableAdons.length === 0 || !subscriptionPlanUUID) return;
    const obj = {
      plan_id: subscriptionPlanUUID,
      types: [availableAdons[0]?.id],
      mode: SUBSCRIPTION_BUY_MODE.ADDON,
    };
    WebService.addLoader("paybtn");
    return WebService.CommonApi({
      method: "POST",
      action: "buy-membership",
      body: obj,
    })
      .then((res: any) => {
        if (res?.status) {
          setShowAddOnModal(false);
          WebService.removeLoader(`button_${index}`);
          const transaction = res?.data?.transaction || res?.transaction;

          setBuySubscriptionDetails((prev: any) => ({
            ...prev,
            client_secret: transaction?.client_secret,
            currency_code: res?.data?.currency,
            exchange_rate: res?.data?.conversion_rate,
            payment_intent: transaction?.payment_intent,
            trans_amount: transaction?.trans_amount,
            trn_id: transaction?.uuid,
          }));

          setShowPaymentForm(true);
        } else {
          WebService.removeLoader("paybtn");
          //  setBuyBtnLoader(false)
          toast.error(res.message);
        }
      })
      .catch((error: any) => {
        WebService.removeLoader(`button_${index}`);
        toast.error(error?.response?.data?.message);
        return error;
      });
  };
  const getAddOnDetails = () => {
    setLoader(true);
    return WebService.getAPI({
      method: "GET",
      action: `membership/addons`,
      isShowError: false,
    })
      .then((res: any) => {
        if (res?.status) {
          setSubscriptionPlanUUID(res?.subscription?.plan?.uuid);
          setavailableAdons(res?.available_addons);
          setCurrencyCode(res?.currency);
          setConversionRate(res?.conversion_rate);
        } else {
          // toast.error(res.message);
        }
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.message);
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const storeImpIdsInRedux = (impIds: RegistrationIds) => {
    dispatch(registrationId(impIds));
  };
  const handleTagNavigation = () => {
    const impIds: RegistrationIds = {
      uuid: profile?.uuid,
      business_uuid: profile?.basic_info?.entrepreneur_business_details?.uuid,
      account_type: account_type,
      login_type: login_type,
    };
    storeImpIdsInRedux(impIds);

    switch (true) {
      case has_investment_subscription && !has_investment_profile_completed:
        navigate("/entrepreneur/investment/register");
        break;

      case has_want_to_sell_subscription && !has_want_to_sell_profile_completed:
        navigate("/entrepreneur/want-to-sell/register");
        break;

      case has_want_to_grow_subscription && !has_want_to_grow_profile_completed:
        navigate("/entrepreneur/want-to-grow/register");
        break;

      // case has_investor_subscription && !has_investor_profile_completed:
      //   navigate('/complete-investor-profile');
      //   break;

      default:
        navigate("/dashboard");
    }
  };
  useEffect(() => {
    if (!access_token_available) return;
    getNotificationCount();

    const intervalId = setInterval(() => {
      getNotificationCount();
    }, 300000);

    return () => clearInterval(intervalId);
  }, []);

  // An admin-created customer with an unresolved mandatory password change can't
  // use any page — bounce them to the change-password screen.
  const location = useLocation();
  useEffect(() => {
    if (
      localStorage.getItem("force_password_change") === "1" &&
      location.pathname !== "/change-password"
    ) {
      navigate("/change-password");
    }
  }, [location.pathname]);
  useEffect(() => {
    const handleNewChatMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      getNotificationCount();
    };

    window.addEventListener("newChatMessage", handleNewChatMessage);

    return () => {
      window.removeEventListener("newChatMessage", handleNewChatMessage);
    };
  }, []);
  const getNotificationCount = () => {
    return WebService.CommonApi({
      method: "GET",
      action: "count/notification",
      body: null,
    })
      .then((res: any) => {
        if (res?.status) {
          setUnreadNotificationCount(res?.unread_notification_count);
          setUnreadChatCount(res?.unread_chat_count);
          setMyProposalCount(res?.unread_proposal_count);

          let ai_scores = res?.ai_scores;

          const profileType =
            login_type === "investor"
              ? "investor"
              : account_type?.toLowerCase();
          console.log(profileType);


          // const score = ai_scores?.[profileType]?.overall?.score ?? 0;
          // const strength = ai_scores?.[profileType]?.overall?.strength ?? "";
          // setAIScore(score);
          // setAIStrength(strength);

          const profiles = Object.entries(ai_scores || {})
            .filter(([_, value]: any) => value?.overall)
            .slice(0, 2)
            .map(([key, value]: any) => {
              const rawScore = value?.overall?.score;
              const parsedScore =
                typeof rawScore === "string"
                  ? Number(rawScore.split("/")[0]) || 0
                  : Number(rawScore ?? 0);
              const parsedMaxScore =
                typeof value?.overall?.max_score === "number"
                  ? value.overall.max_score
                  : typeof rawScore === "string" && rawScore.includes("/")
                  ? Number(rawScore.split("/")[1]) || 1000
                  : 1000;

              return {
                profile: key, // <-- route/profile type
                label: AI_PROFILE_LABELS[key] ?? key,
                score: parsedScore,
                max_score: parsedMaxScore,
                strength: value?.overall?.strength ?? value?.overall?.label ?? "",
              };
            });
          // profiles[1].score = 88;
          // profiles[1].strength = 'strong';

          setAIProfiles(profiles);
          dispatch(storeAIScore(ai_scores));
        }
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.message);
        return error;
      });
  };

  const frontStatus = getProfileStatus(aiProfiles[0]?.score ?? 0, aiProfiles[0]?.strength ?? "");
  const backStatus = getProfileStatus(aiProfiles[1]?.score ?? 0, aiProfiles[1]?.strength ?? "");

  if (location.pathname.includes("/profile-review")) {
    const getProfileTitle = () => {
      const path = location.pathname.toLowerCase();
      if (path.includes("investor")) return "Investor Profile Review";
      if (path.includes("investment")) return "Capital Bridge — Profile Review";
      if (path.includes("want-to-sell")) return "Business Exchange — Profile Review";
      if (path.includes("want-to-grow")) return "Global Connect — Profile Review";
      return "Profile Review";
    };

    return (
      <div className="site-header profile-review-site-header">
        <div className="pr-top-bar">
          <Container>
            {/* Top Row: Logo, Step Badge & AI Status */}
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center gap-3">
                <img src={Logo} alt="The Prime Shark" className="pr-brand-logo" />
                <span className="pr-step-badge d-none d-sm-inline-flex align-items-center gap-1.5">
                  <MdAutoAwesome size={13} />
                  Final Step • Profile Verification
                </span>
              </div>
              <div className="pr-status-pill d-flex align-items-center">
                <span className="dot" />
                <span>AI Assessment Ready</span>
              </div>
            </div>

            {/* Heading Row */}
            <div className="pt-1">
              <h1 className="pr-page-title mb-1">
                {getProfileTitle()}
              </h1>
              <p className="pr-page-desc mb-0">
                Verify your submitted profile details below before exploring your Prime Shark Intelligence report.
              </p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="site-header">
        <Navbar collapseOnSelect expand="lg">
          <Container>
            <Navbar.Brand href="/">
              {" "}
              <img src={Logo} width={187} height={52} alt="" />
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="responsive-navbar-nav" />
            <Navbar.Collapse id="responsive-navbar-nav">
              <Nav className="me-auto mx-auto">
                <Nav.Link
                  // href={"javascript:void(0)"}
                  onClick={() => {
                    storeTabDeails("home");
                    {
                      login_type?.toLocaleLowerCase() ===
                        LOGIN_TYPE.ENTREPRENEUR.toLocaleLowerCase()
                        ? navigate(
                          `/${login_type?.toLocaleLowerCase()}/${account_type?.toLocaleLowerCase()}/home`,
                        )
                        : navigate(`/${login_type?.toLocaleLowerCase()}/home`);
                    }
                  }}
                  className={
                    selectedTab === "home" ? "homeIcon active" : "homeIcon"
                  }
                >
                  <span>Home</span>
                </Nav.Link>

                {login_type?.toLocaleLowerCase() ===
                  LOGIN_TYPE.ENTREPRENEUR.toLocaleLowerCase() && (
                    <Nav.Link
                      // href={"javascript:void(0)"}
                      onClick={() => {
                        storeTabDeails("Investor");
                        navigate(
                          `/${login_type?.toLocaleLowerCase()}/${account_type?.toLocaleLowerCase()}/investors`,
                        );
                      }}
                      className={
                        selectedTab === "Investor"
                          ? "investorIcon active"
                          : "investorIcon"
                      }
                    >
                      <span>Investors</span>
                    </Nav.Link>
                  )}

                {login_type === LOGIN_TYPE.INVESTOR.toLocaleLowerCase() && (
                  <>
                    <Nav.Link
                      // href={"javascript:void(0)"}
                      onClick={() => {
                        if (isInvestorUnderReview) {
                          setShowProfileReviewModal(true);
                          return;
                        }
                        storeTabDeails("capital_bridge");
                        navigate(`/${login_type}/capital-bridge`);
                      }}
                      className={
                        selectedTab === "capital_bridge"
                          ? "businessIcon1 active"
                          : "businessIcon1"
                      }
                    >
                      <span>Explore</span>
                    </Nav.Link>
                    {/* <Nav.Link
                      //  href={"javascript:void(0)"}
                      onClick={() => {
                        storeTabDeails("business_exchange");
                        navigate(`/${login_type}/business-exchange`);
                      }}
                      className={
                        selectedTab === "business_exchange"
                          ? "businessIcon active"
                          : "businessIcon"
                      }
                    >
                      <span>Deals</span>
                    </Nav.Link> */}
                  </>
                )}

                {login_type?.toLowerCase() ===
                  LOGIN_TYPE.ENTREPRENEUR.toLowerCase() && (
                    <Nav.Link
                      // href={`/${login_type?.toLocaleLowerCase()}/${account_type?.toLocaleLowerCase()}/global-connect`}
                      onClick={() => {
                        handleGlobalconnectNavigation();
                      }}
                      // disabled={has_want_to_grow_access === 0}
                      className={
                        selectedTab === "global-connect"
                          ? "businessIcon active"
                          : "businessIcon"
                      }
                    >
                      <span className="text-nowrap">Network</span>
                    </Nav.Link>
                  )}

                <Nav.Link
                  onClick={() => {
                    if (isInvestorUnderReview) {
                      setShowProfileReviewModal(true);
                      return;
                    }
                    storeTabDeails("Chat");
                    login_type && login_type !== "investor"
                      ? navigate(
                        `/${login_type?.toLocaleLowerCase()}/${account_type?.toLocaleLowerCase()}/chats`,
                      )
                      : navigate(`/${login_type?.toLocaleLowerCase()}/chats`);
                  }}
                  className={
                    selectedTab === "Chat" ? "chatsIcon active" : "chatsIcon"
                  }
                >
                  <span className="chat-text position-relative">
                    Messages
                    {unreadChatCount > 0 && (
                      <span className="proposal-badge">
                        {unreadChatCount > 10 ? "10+" : unreadChatCount}
                      </span>
                    )}
                  </span>
                </Nav.Link>
                {login_type !== LOGIN_TYPE.INVESTOR.toLocaleLowerCase() ? (
                  <Nav.Link
                    onClick={() => {
                      storeTabDeails("Proposal");
                      navigate(
                        `/${login_type?.toLocaleLowerCase()}/${account_type?.toLocaleLowerCase()}/sent-proposals`,
                      );
                    }}
                    className={
                      selectedTab === "Proposal"
                        ? "sentIcon active"
                        : "sentIcon"
                    }
                  >
                    <span className="text-nowrap">Proposals</span>
                  </Nav.Link>
                ) : (
                  <Nav.Link
                    onClick={() => {
                      storeTabDeails("Proposal");
                      navigate(
                        `/${login_type?.toLocaleLowerCase()}/my-proposals`,
                      );
                    }}
                    className={
                      selectedTab === "Proposal"
                        ? "sentIcon active"
                        : "sentIcon"
                    }
                  >
                    <span className="position-relative">
                      Proposals
                      {myProposalCount > 0 && (
                        <span className="proposal-badge">
                          {myProposalCount > 10 ? "10+" : myProposalCount}
                        </span>
                      )}
                    </span>
                  </Nav.Link>
                )}

                {/* {login_type !== LOGIN_TYPE.INVESTOR.toLocaleLowerCase() && (
                  <SwitchProfile />
                )} */}
              </Nav>
            </Navbar.Collapse>
            <NotificationBell count={unreadNotificationCount} />
            <RightDropdownMenu
              login_type={login_type ?? ""}
              account_type={account_type ?? ""}
            />
            {/* <div className="d-flex justify-content-end ms-5">
              <button
                className="ai-analysis-btn"
                onClick={() => {
                  if (account_type) {
                    navigate(`${login_type}/${account_type}/profile-analysis`);
                  } else {
                    navigate(`${login_type}/profile-analysis`);
                  }
                }}
              >
                <div className="ai-ring">
                  <div className="ai-inner">
                    <span className="score">{aiScore}</span>
                    <div className="ai-meta">
                      <span className="label">AI</span>
                      <span className="status">
                        {HelperService.ProperCase(aiStrength || "")}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            </div> */}

            <div className="ai-score-wrapper d-flex justify-content-end align-items-center ms-5">

              {aiProfiles.length > 0 && (
                <div>
                  <button
                    className={
                      selectedTab === "score"
                        ? "ai-analysis-btn active"
                        : "ai-analysis-btn"
                    }
                    onClick={() => handleAINavigation(aiProfiles[visibleAIIndex]?.profile)}
                  >
                    <div className="ai-ring">
                      <div
                        className={`flip-card-inner ${isFlipped ? "is-flipped" : ""}`}
                        onTransitionEnd={() => {
                          setVisibleAIIndex((prev) => (prev + 1) % aiProfiles.length);
                        }}
                        style={{
                          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                        }}
                      >
                        {/* Front Side */}
                        <div className="flip-face front" style={frontStatus.metalColors}>
                          <div className="dial">
                            <svg className="ticks-svg" viewBox="0 0 200 200" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
                              {DIAL_TICKS}
                            </svg>
                            <svg className="ring-svg" viewBox="0 0 200 200">
                              <defs>
                                <linearGradient id="gradFront" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor={frontStatus.gradientColors[0]} />
                                  <stop offset="100%" stopColor={frontStatus.gradientColors[1]} />
                                </linearGradient>
                              </defs>
                              <circle className="ring-track" cx="100" cy="100" r="86" />
                              <circle
                                className="ring-fill"
                                cx="100"
                                cy="100"
                                r="86"
                                stroke="url(#gradFront)"
                                strokeDasharray="540.4"
                                strokeDashoffset={
                                  540.4 -
                                  540.4 *
                                    Math.min(
                                      1,
                                      Math.max(
                                        0,
                                        (aiProfiles[0]?.score ?? 0) /
                                          (aiProfiles[0]?.max_score || 1000)
                                      )
                                    )
                                }
                              />
                            </svg>
                            <div className="glass" />
                            <div className="coin-sheen" />
                            <div className="ring-content">
                              <LightbulbIcon />
                              <div
                                className="score"
                                style={{
                                  color: frontStatus.textColor,
                                  fontSize: frontScore >= 1000 ? "22px" : "26px",
                                }}
                              >
                                {frontScore}
                              </div>
                              <div className="status" style={{ color: "#ffffff" }}>
                                {frontStatus.label}
                              </div>
                              <div style={{ width: '40px', height: '1px', backgroundColor: '#caa542', margin: '4px 0' }} />
                              <StarsIcon />
                            </div>
                          </div>
                        </div>

                        {/* Back Side */}
                        {aiProfiles.length > 1 && (
                          <div className="flip-face back" style={backStatus.metalColors}>
                            <div className="dial">
                              <svg className="ticks-svg" viewBox="0 0 200 200" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
                                {DIAL_TICKS}
                              </svg>
                              <svg className="ring-svg" viewBox="0 0 200 200">
                                <defs>
                                  <linearGradient id="gradBack" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor={backStatus.gradientColors[0]} />
                                    <stop offset="100%" stopColor={backStatus.gradientColors[1]} />
                                  </linearGradient>
                                </defs>
                                <circle className="ring-track" cx="100" cy="100" r="86" />
                                <circle
                                  className="ring-fill"
                                  cx="100"
                                  cy="100"
                                  r="86"
                                  stroke="url(#gradBack)"
                                  strokeDasharray="540.4"
                                  strokeDashoffset={
                                    540.4 -
                                    540.4 *
                                      Math.min(
                                        1,
                                        Math.max(
                                          0,
                                          (aiProfiles[1]?.score ?? 0) /
                                            (aiProfiles[1]?.max_score || 1000)
                                        )
                                      )
                                  }
                                />
                              </svg>
                              <div className="glass" />
                              <div className="coin-sheen" />
                              <div className="ring-content">
                                <LightbulbIcon />
                                <div
                                  className="score"
                                  style={{
                                    color: backStatus.textColor,
                                    fontSize: backScore >= 1000 ? "22px" : "26px",
                                  }}
                                >
                                  {backScore}
                                </div>
                                <div className="status" style={{ color: "#ffffff" }}>
                                  {backStatus.label}
                                </div>
                                <div style={{ width: '40px', height: '1px', backgroundColor: '#caa542', margin: '4px 0' }} />
                                <StarsIcon />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                  </button>
                  <div className="profile-label-below">
                    {aiProfiles[visibleAIIndex]?.label}
                  </div>
                </div>
              )}
            </div>
          </Container>
        </Navbar>

        {/* <div className="d-flex justify-content-center w-100 mt-0" onClick={() => handleTagNavigation()}>
          {incompleteProfiles.length > 0 && (
            <div className="complete-profile-tag">
              ⚠️ Complete your {incompleteProfiles.join(' & ')}
            </div>
          )}
        </div> */}
      </div>

      <ConfirmSubscriptionModal
        show={showModal}
        message={modalMessage}
        onConfirm={handleYes}
        onCancel={() => setShowModal(false)}
      />
      <AddOnConfirmationModal
        loader={loader}
        show={showAddOnModal}
        addon={availableAdons}
        conversionRate={conversionRate}
        currencyCode={currencyCode}
        message="You don’t have this feature in your subscription. Do you want to add this feature to your current subscription?"
        onConfirm={handleYesForAddOn}
        onCancel={() => setShowAddOnModal(false)}
      />
      {buySubscriptionDetails?.client_secret &&
        buySubscriptionDetails?.payment_intent &&
        buySubscriptionDetails.trn_id && (
          <Modal
            show={showPaymentForm}
            onHide={() => setShowPaymentForm(false)}
            backdrop="static"
            keyboard={false}
          >
            <Modal.Header closeButton>Payment</Modal.Header>
            <Modal.Body>
              <Payment
                {...buySubscriptionDetails}
                onSuccess={() => setShowPaymentForm(false)}
              />
            </Modal.Body>
          </Modal>
        )}

      <ProfileUnderReviewModal
        show={showProfileReviewModal}
        onHide={() => setShowProfileReviewModal(false)}
      />
    </>
  );
};
export default React.memo(Header);
